import {
  InventoryTransactionType,
  ProductInboundTransactionDoc,
  ProductOutboundTransactionDoc,
  ProductTransactionStatus,
} from '@gooduncles/gu-app-schema';
import { ColDef, ITooltipParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { FC, useMemo } from 'react';

import {
  formatNumberForAgCell,
  getCellStyleForTransactionStatus,
  productTransactionStatusFormatter,
  stringDateFormatter,
} from 'src/utils/ag-grid-util';
import { formatNumber } from 'src/utils/common';

import { InventoryIssueItem } from 'src/hooks/subscription/useInventoryIssuesWithTransaction';

import DownloadRawDataCell from 'src/atomic-components/atoms/AgCell/AgCellDownloadRawData';
import AgCellInventory from 'src/atomic-components/atoms/AgCell/AgCellInventory';
import AgCellUser from 'src/atomic-components/atoms/AgCell/AgCellUser';

const defaultColDef = {
  sortable: true,
  resizable: true,
  filter: true,
};

interface InventoryIssueTableProps {
  rowData: InventoryIssueItem[] | null;
}

const InventoryIssueTable: FC<InventoryIssueTableProps> = ({ rowData }) => {
  const columnDefs: ColDef<InventoryIssueItem>[] = useMemo(
    () => [
      {
        headerName: 'Id',
        field: '_id',
        width: 80,
        hide: true,
      },
      {
        headerName: 'transactionId',
        field: 'transaction._id',
        width: 80,
        hide: true,
      },
      {
        headerName: 'inventoryId',
        field: 'transaction.inventoryId',
        width: 80,
        hide: true,
      },
      {
        headerName: '이슈 일자',
        field: 'issueDate',
        width: 120,
        valueFormatter: stringDateFormatter,
      },
      {
        headerName: '재고명',
        field: 'inventoryName',
        width: 240,
        cellRenderer: AgCellInventory,
        cellRendererParams: (params: any) => ({
          inventoryId: params.data?.transaction?.inventoryId,
          field: 'inventoryName',
        }),
      },
      {
        headerName: '수량',
        field: 'transaction.requestValue',
        headerTooltip:
          '입고 요청에는 요청과 단위 비율을 함께 표시할 수 있으나, 출고 요청에서는 발주 환산을 위해 실제 출고량만 기록함으로 해당 값이 표시되지않습니다.',
        valueGetter: (params) => {
          const { data } = params;
          if (!data?.transaction) return null;

          // inbound인 경우 requestValue 활용, outbound인 경우 requestQuantity 활용
          if (data.transactionType === InventoryTransactionType.INBOUND) {
            const transaction = data.transaction as ProductInboundTransactionDoc;
            return transaction.requestValue * transaction.inventoryUnitRatio;
          } else if (data.transactionType === InventoryTransactionType.OUTBOUND) {
            const transaction = data.transaction as ProductOutboundTransactionDoc;
            return transaction.requestQuantity;
          }
          return null;
        },
        valueFormatter: formatNumberForAgCell,
        type: 'numericColumn',
        width: 120,
        cellRenderer: (params: any) => {
          const formattedValue = params.value === Infinity ? '∞' : formatNumber(params.value);
          const type: string = params.data?.transactionType;
          const status: ProductTransactionStatus = params.data?.transaction?.status;
          if (type === InventoryTransactionType.INBOUND) {
            const transaction = params.data.transaction as ProductInboundTransactionDoc;
            return (
              <span className={`stock-cell ${type?.toLowerCase() ?? ''} ${status}`}>
                {formattedValue} ({transaction.requestValue}x{transaction.inventoryUnitRatio})
              </span>
            );
          }
          return <span className={`stock-cell ${type?.toLowerCase() ?? ''} ${status}`}>{formattedValue}</span>;
        },
      },
      {
        headerName: '상태',
        field: 'transaction.status',
        width: 120,
        valueFormatter: productTransactionStatusFormatter,
        cellStyle: getCellStyleForTransactionStatus,
        filterParams: {
          valueFormatter: productTransactionStatusFormatter,
        },
        tooltipValueGetter: (params: ITooltipParams<InventoryIssueItem, ProductTransactionStatus>) => {
          if (
            params.value &&
            [ProductTransactionStatus.REJECTED, ProductTransactionStatus.FAILED].includes(params.value)
          ) {
            return params.data?.transaction?.error;
          }
          return null;
        },
      },
      {
        headerName: '이슈 내용',
        field: 'reason',
        width: 300,
      },
      {
        headerName: '작성자',
        field: 'email',
        width: 200,
        cellRenderer: AgCellUser,
        cellRendererParams: (params: any) => ({
          userId: params.data?.createdBy,
          field: 'email',
        }),
      },
      {
        headerName: '액션',
        minWidth: 120,
        cellRenderer: DownloadRawDataCell,
        hide: true,
      },
    ],
    []
  );

  return (
    <div className='ag-theme-alpine height100'>
      <AgGridReact
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        enableCellTextSelection={true}
        tooltipShowDelay={500}
      />
    </div>
  );
};

export default InventoryIssueTable;
